import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AreaInput,
  AreaView,
  DivisionTree,
  UpdateAreaInput,
  UpdateWardInput,
  UpdateZoneInput,
  WardBoundaryImport,
  WardInput,
  WardView,
  ZoneInput,
  ZoneView,
} from '@vmc/shared';
import { useConfirm } from '@/components/ui/confirm';
import { api } from '@/lib/api';
import { UNDO_DELETE_MS, undoableDelayed } from '@/lib/feedback';
import { useCallback } from 'react';
import { hideRow, useHidden, type HiddenRow } from '@/lib/optimistic';

export const divisionKeys = { tree: ['divisions'] as const };

/** Leaves out zones, wards and areas whose delete is still inside its undo window. */
function withoutHidden(tree: DivisionTree, hidden: ReadonlySet<string>): DivisionTree {
  if (!hidden.size) return tree;
  const isHidden = (id: string) => hidden.has(id);
  return {
    ...tree,
    zones: tree.zones
      .filter((z) => !isHidden(z.id))
      .map((z) => ({
        ...z,
        wards: z.wards
          .filter((w) => !isHidden(w.id))
          .map((w) => ({ ...w, areas: w.areas.filter((a) => !isHidden(a.id)) })),
      })),
  };
}

/** The tree is small and rarely changes; every filter and form shares this one cached copy. */
export function useDivisions() {
  const hidden = useHidden();
  const select = useCallback((t: DivisionTree) => withoutHidden(t, hidden), [hidden]);
  return useQuery({
    queryKey: divisionKeys.tree,
    queryFn: ({ signal }) => api.get<DivisionTree>('/api/divisions', undefined, signal),
    select,
    staleTime: 5 * 60_000,
  });
}

/** Lookup maps for rendering names from ids. */
export function indexDivisions(tree: DivisionTree | undefined) {
  const zones = new Map<string, ZoneView>();
  const wards = new Map<string, WardView>();
  const areas = new Map<string, AreaView>();
  for (const z of tree?.zones ?? []) {
    zones.set(z.id, z);
    for (const w of z.wards) {
      wards.set(w.id, w);
      for (const a of w.areas) areas.set(a.id, a);
    }
  }
  return { zones, wards, areas };
}

export const wardLabel = (w: { number: number; nameEn: string }): string =>
  w.nameEn.toLowerCase() === `ward ${w.number}` ? w.nameEn : `Ward ${w.number} · ${w.nameEn}`;

function useInvalidateDivisions() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: divisionKeys.tree });
    // Citizen rows embed zone/ward/area names and counts.
    void qc.invalidateQueries({ queryKey: ['citizens'] });
  };
}

type Level = 'zones' | 'wards' | 'areas';
type LevelInput = { zones: ZoneInput; wards: WardInput; areas: AreaInput };
type LevelUpdate = { zones: UpdateZoneInput; wards: UpdateWardInput; areas: UpdateAreaInput };

export function useSaveDivision<L extends Level>(level: L) {
  const invalidate = useInvalidateDivisions();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: LevelInput[L] | LevelUpdate[L] }) =>
      id
        ? api.patch(`/api/divisions/${level}/${id}`, input)
        : api.post(`/api/divisions/${level}`, input),
    onSuccess: invalidate,
    meta: { errorToast: false },
  });
}

/** Deletes after a confirm, then an undo window (DESIGN.md rule 5); it leaves the tree at once. */
export function useDeleteDivision() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  return async (level: Level, id: string, name: string, description?: string) => {
    const ok = await confirm({ title: `Delete ${name}?`, description, confirmLabel: 'Delete' });
    if (!ok) return;
    let row: HiddenRow | undefined;
    undoableDelayed({
      message: `${name} deleted`,
      description,
      ms: UNDO_DELETE_MS,
      apply: () => {
        row = hideRow(id);
      },
      revert: () => row?.restore(),
      commit: async () => {
        await api.delete(`/api/divisions/${level}/${id}`);
        void qc.invalidateQueries({ queryKey: ['citizens'] });
        await row?.settle(() => qc.invalidateQueries({ queryKey: divisionKeys.tree }));
      },
    });
  };
}

export function useImportBoundaries() {
  const invalidate = useInvalidateDivisions();
  return useMutation({
    mutationFn: ({ geojson, property }: { geojson: WardBoundaryImport; property: string }) =>
      api.post<{ updated: number; unmatched: string[] }>(
        '/api/divisions/wards/boundaries',
        geojson,
        { property },
      ),
    onSuccess: invalidate,
  });
}

export function useReassignByLocation() {
  const invalidate = useInvalidateDivisions();
  return useMutation({
    mutationFn: () => api.post<{ reassigned: number }>('/api/divisions/wards/reassign-by-location'),
    onSuccess: invalidate,
  });
}
